import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { getLessonQuiz, submitQuizResults, submitQuizResultsAuth } from '../../services/api';
import DarkModeToggle from '../../components/DarkModeToggle/DarkModeToggle';
import './Quiz.css';

const Quiz = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { handleRequest, loading } = useApi();
  const { user } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const fetchLesson = async () => {
      try {
        const response = await handleRequest(getLessonQuiz, id);
        const lessonData = response.data;
        setLesson(lessonData);
        
        // Load saved progress from localStorage
        const savedProgress = localStorage.getItem(`quiz-progress-${id}`);
        if (savedProgress && lessonData.quiz) {
          try {
            const progress = JSON.parse(savedProgress);
            
            // Validate and restore answers (only if quiz structure matches)
            if (progress.answers && lessonData.quiz.length > 0) {
              const validAnswers = {};
              // Only restore answers for questions that still exist
              Object.keys(progress.answers).forEach(key => {
                const questionIndex = parseInt(key);
                if (questionIndex >= 0 && questionIndex < lessonData.quiz.length) {
                  validAnswers[key] = progress.answers[key];
                }
              });
              setAnswers(validAnswers);
            }
            
            // Validate and restore current question index
            if (typeof progress.currentQuestion === 'number' && 
                progress.currentQuestion >= 0 && 
                progress.currentQuestion < lessonData.quiz.length) {
              setCurrentQuestion(progress.currentQuestion);
            }
          } catch (e) {
            console.error('Failed to load saved quiz progress:', e);
            // Clear invalid saved progress
            localStorage.removeItem(`quiz-progress-${id}`);
          }
        }
      } catch (error) {
        console.error('Failed to fetch lesson:', error);
      }
    };

    fetchLesson();
  }, [id, handleRequest]);

  // Auto-save progress to localStorage whenever answers or currentQuestion changes
  useEffect(() => {
    if (lesson && id && !submitted) {
      const progress = {
        answers: answers,
        currentQuestion: currentQuestion,
        timestamp: new Date().toISOString()
      };
      localStorage.setItem(`quiz-progress-${id}`, JSON.stringify(progress));
    }
  }, [answers, currentQuestion, lesson, id, submitted]);

  const handleAnswerChange = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [currentQuestion]: answer
    }));
  };

  const handleNext = () => {
    if (currentQuestion < lesson.quiz.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    
    // Clear saved progress from localStorage since quiz is being submitted
    if (id) {
      localStorage.removeItem(`quiz-progress-${id}`);
    }
    
    const results = {
      lessonId: id,
      answers: lesson.quiz.map((_question, index) => answers[index]),
      timestamp: new Date().toISOString()
    };

    try {
      // Always try auth endpoint first - it will use the auth cookie if available
      // This ensures student ID is saved for progress tracking when user is logged in
      // If auth fails (401), fall back to anonymous endpoint
      try {
        const response = await handleRequest(submitQuizResultsAuth, id, results);
        navigate(`/results/${response.data.resultId}`, { state: { results: response.data.results } });
        return;
      } catch (authError) {
        // If auth fails (401/403), user is not authenticated, use anonymous endpoint
        if (authError.response?.status === 401 || authError.response?.status === 403) {
          console.log('User not authenticated, using anonymous endpoint');
          const response = await handleRequest(submitQuizResults, id, results);
          navigate(`/results/${response.data.resultId}`, { state: { results: response.data.results } });
          return;
        }
        // If it's a different error, throw it
        throw authError;
      }
    } catch (error) {
      console.error('Failed to submit results:', error);
      navigate('/results', {
        state: { results }
      });
    }
  };

  if (loading || !lesson) {
    return <div className="quiz-container">Laster...</div>;
  }

  const currentQuizQuestion = lesson.quiz?.[currentQuestion];

  return (
    <div className="quiz-container">
      <header className="quiz-header">
        <div className="quiz-header-top">
          <button 
            type="button" 
            className="back-button" 
            onClick={() => navigate('/dashboard')}
            title="Tilbake til oversikten"
          >
            ← Tilbake
          </button>
          <h1>{lesson.title} - Quiz</h1>
          <DarkModeToggle />
        </div>
        <div className="quiz-progress">
          Spørsmål {currentQuestion + 1} av {lesson.quiz?.length}
        </div>
      </header>

      <main className="quiz-main">
        {currentQuizQuestion && (
          <div className="question-container">
            <h2>Spørsmål {currentQuestion + 1}</h2>
            <p className="question-text">{currentQuizQuestion.question}</p>

            <div className="answer-section">
              {currentQuizQuestion.type === 'multiple-choice' && (
                <>
                  <div className="options">
                    {currentQuizQuestion.options.map((option, index) => (
                      <label 
                        key={index} 
                        className={`option-label 
                          ${answers[currentQuestion] === option ? 'selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`question-${currentQuestion}`}
                          value={option}
                          checked={answers[currentQuestion] === option}
                          onChange={() => handleAnswerChange(option)}
                          disabled={submitted}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}

              {currentQuizQuestion.type === 'true-false' && (
                <>
                  <div className="options">
                    {['True', 'False'].map((option) => (
                      <label 
                        key={option} 
                        className={`option-label 
                          ${answers[currentQuestion] === option ? 'selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`question-${currentQuestion}`}
                          value={option}
                          checked={answers[currentQuestion] === option}
                          onChange={() => handleAnswerChange(option)}
                          disabled={submitted}
                        />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}

              {(currentQuizQuestion.type === 'short-answer' || 
                currentQuizQuestion.type === 'essay') && (
                <textarea
                  value={answers[currentQuestion] || ''}
                  onChange={(e) => handleAnswerChange(e.target.value)}
                  disabled={submitted}
                  rows={currentQuizQuestion.type === 'essay' ? 6 : 2}
                  placeholder="Skriv inn svaret ditt her..."
                />
              )}
            </div>

            {submitted && (
              <div className="feedback">
                <p>Levert! Sjekk resultatsiden for detaljert tilbakemelding.</p>
              </div>
            )}
          </div>
        )}

        <div className="quiz-navigation">
          <button 
            onClick={handlePrevious} 
            disabled={currentQuestion === 0}
            className="nav-btn"
          >
            Forrige
          </button>
          <button 
            onClick={handleNext} 
            disabled={currentQuestion === lesson.quiz.length - 1}
            className="nav-btn"
          >
            Neste
          </button>
          {!submitted && (
            <button 
              onClick={handleSubmit}
              className="submit-btn"
              disabled={Object.keys(answers).length !== lesson.quiz.length}
            >
              Lever quiz
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default Quiz; 